/**
 * G-84 — a storage-box label on a 58 mm strip.
 *
 * PURE, measurer-injected, like `noteLayout.ts` and for the same reason: the
 * receipt layout is welded to OD-13's locked footer and fixed blocks, and a
 * label has none of that. Top to bottom: the code, large; the optional name;
 * the barcode with the code in plain text beneath it (a person can read what
 * a dead scanner cannot); the shop and the kept-in zone, small.
 */
import { encodeCode128, pickModuleDots, QUIET_ZONE_MODULES, type EncodedCode128 } from "./code128";

export type MeasureLabelText = (text: string, px: number, weight: "normal" | "bold") => number;

export interface LabelTextOp {
  text: string;
  x: number;
  /** Baseline. */
  y: number;
  px: number;
  weight: "normal" | "bold";
  align: "center";
}

export interface LabelBarcodeOp {
  encoded: EncodedCode128;
  x: number;
  y: number;
  height: number;
  moduleDots: number;
  tooNarrow: boolean;
}

export interface LaidOutLabel {
  width: number;
  height: number;
  texts: LabelTextOp[];
  barcode: LabelBarcodeOp;
}

export interface LabelInput {
  code: string;
  barcodeText: string;
  name: string | null;
  shopName: string;
  keptInZoneName: string | null;
}

/**
 * G-84 follow-up (owner 2026-09-17) — an ITEM's own barcode on a sticker, so
 * a product that arrived without a readable label can be given one.
 *
 * Different shape from the box label and deliberately so: on a box the CODE
 * is the headline and the name is a hint; on an item the NAME is what a
 * florist reads and the digits are for the scanner. The name gets two lines
 * because product names are long ("PISELLO ODOROSO MAZZO X4 CON FOGLIE"),
 * where a box code never is.
 */
export interface ItemLabelInput {
  itemName: string;
  barcodeText: string;
}

/** Clear of both edges; thermal heads are unreliable at the rim. */
const MARGIN = 10;
const CODE_PX = 40;
const NAME_PX = 28;
const NAME_MIN_PX = 20;
const HUMAN_PX = 22;
const SMALL_PX = 18;
const BAR_HEIGHT = 110;
const GAP = 12;
const LINE = 1.3;

function fitOneLine(text: string, maxPx: number, minPx: number, width: number, measure: MeasureLabelText, weight: "normal" | "bold"): { px: number; text: string } {
  for (let px = maxPx; px >= minPx; px -= 2) {
    if (measure(text, px, weight) <= width) return { px, text };
  }
  // Still too long at the floor: keep the start, mark the cut.
  let cut = text;
  while (cut.length > 3 && measure(`${cut}…`, minPx, weight) > width) cut = cut.slice(0, -1);
  return { px: minPx, text: `${cut}…` };
}

/**
 * Greedy wrap to at most `maxLines`; the last line takes the ellipsis when
 * words were left over.
 *
 * Two things here were wrong once and are worth keeping right:
 *
 * 1. Whether text was LOST is counted in WORDS, not in string length. The
 *    joined lines normalise whitespace, so a name with a double space —
 *    ordinary in anything parsed out of a supplier PDF — lost a character to
 *    the join and the label claimed a truncation that never happened, on a
 *    sticker that then stays on the product.
 * 2. A word WIDER THAN THE LABEL is cut here rather than accepted. It used to
 *    be taken whole when the line was empty, so an unbroken token — a SKU
 *    used as a name, a run-together supplier string — laid out wider than the
 *    paper and the head printed its middle with both ends missing and nothing
 *    said so. The box label never had that hole (`fitOneLine` shrinks, then
 *    cuts); the item sticker must not either.
 */
function cutToWidth(
  word: string,
  px: number,
  width: number,
  measure: MeasureLabelText,
  weight: "normal" | "bold",
): string {
  if (measure(word, px, weight) <= width) return word;
  let cut = word;
  while (cut.length > 1 && measure(`${cut}…`, px, weight) > width) cut = cut.slice(0, -1);
  return `${cut}…`;
}

function fitLines(
  text: string,
  px: number,
  width: number,
  measure: MeasureLabelText,
  weight: "normal" | "bold",
  maxLines: number,
): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length === 0) return [text.trim()];

  const lines: string[] = [];
  let line = "";
  let used = 0;

  for (const word of words) {
    if (lines.length >= maxLines) break;
    // Every word is brought within the label BEFORE it is placed, so no line
    // can ever be wider than the paper.
    const fitted = cutToWidth(word, px, width, measure, weight);
    const candidate = line ? `${line} ${fitted}` : fitted;
    if (measure(candidate, px, weight) <= width) {
      line = candidate;
      used += 1;
      continue;
    }
    lines.push(line);
    if (lines.length >= maxLines) break;
    line = fitted;
    used += 1;
  }
  if (lines.length < maxLines && line) lines.push(line);
  if (lines.length === 0) return [text.trim()];

  // Words left OVER — counted in words, never in characters: joining the
  // lines normalises whitespace, so a name with a double space used to be
  // reported as truncated when every word of it had printed.
  const last = lines[lines.length - 1];
  if (used < words.length && !last.endsWith("…")) {
    let trimmed = last;
    while (trimmed.length > 1 && measure(`${trimmed}…`, px, weight) > width) {
      trimmed = trimmed.slice(0, -1);
    }
    lines[lines.length - 1] = `${trimmed}…`;
  }
  return lines;
}

/**
 * The item sticker: name (up to two lines), the barcode, the digits beneath.
 *
 * No price, by decision — a sticker outlives the price it was printed with,
 * and the card on the tablet always has the live one. Nothing here is a
 * retail EAN symbol: the digits are encoded as Code128, which the kiosk's own
 * scanner reads back identically, and that is what this label is for.
 */
export function layoutItemLabel(
  input: ItemLabelInput,
  widthDots: number,
  measure: MeasureLabelText,
): LaidOutLabel {
  const inner = widthDots - 2 * MARGIN;
  const centre = widthDots / 2;
  const texts: LabelTextOp[] = [];
  let y = MARGIN;

  const namePx = NAME_PX;
  for (const line of fitLines(input.itemName.trim(), namePx, inner, measure, "bold", 2)) {
    y += Math.round(namePx * LINE);
    texts.push({ text: line, x: centre, y, px: namePx, weight: "bold", align: "center" });
  }

  y += GAP;
  const encoded = encodeCode128(input.barcodeText);
  const { dots, tooNarrow } = pickModuleDots(encoded, inner);
  const barWidth = encoded.modules * dots;
  const barcode: LabelBarcodeOp = {
    encoded,
    x: Math.round(centre - barWidth / 2),
    y,
    height: BAR_HEIGHT,
    moduleDots: dots,
    tooNarrow,
  };
  y += BAR_HEIGHT + Math.round(HUMAN_PX * 1.1);
  texts.push({
    text: input.barcodeText,
    x: centre,
    y,
    px: HUMAN_PX,
    weight: "normal",
    align: "center",
  });

  y += MARGIN;
  return { width: widthDots, height: y, texts, barcode };
}

export function layoutLabel(input: LabelInput, widthDots: number, measure: MeasureLabelText): LaidOutLabel {
  const inner = widthDots - 2 * MARGIN;
  const centre = widthDots / 2;
  const texts: LabelTextOp[] = [];
  let y = MARGIN;

  const code = fitOneLine(input.code, CODE_PX, 24, inner, measure, "bold");
  y += code.px;
  texts.push({ text: code.text, x: centre, y, px: code.px, weight: "bold", align: "center" });

  if (input.name && input.name.trim()) {
    const name = fitOneLine(input.name.trim(), NAME_PX, NAME_MIN_PX, inner, measure, "normal");
    y += Math.round(name.px * LINE);
    texts.push({ text: name.text, x: centre, y, px: name.px, weight: "normal", align: "center" });
  }

  y += GAP;
  const encoded = encodeCode128(input.barcodeText);
  const { dots, tooNarrow } = pickModuleDots(encoded, inner);
  const barWidth = encoded.modules * dots;
  const barcode: LabelBarcodeOp = {
    encoded,
    x: Math.round(centre - barWidth / 2),
    y,
    height: BAR_HEIGHT,
    moduleDots: dots,
    tooNarrow,
  };
  // The spec's quiet zone lives inside the margin only if the margin is wide
  // enough; otherwise the paper edge is the quiet zone, which also works.
  void QUIET_ZONE_MODULES;
  y += BAR_HEIGHT + Math.round(HUMAN_PX * 1.1);
  texts.push({ text: input.barcodeText, x: centre, y, px: HUMAN_PX, weight: "normal", align: "center" });

  y += GAP;
  const footer = [input.shopName, input.keptInZoneName].filter(Boolean).join(" · ");
  const small = fitOneLine(footer, SMALL_PX, 14, inner, measure, "normal");
  y += Math.round(small.px * LINE);
  texts.push({ text: small.text, x: centre, y, px: small.px, weight: "normal", align: "center" });

  y += MARGIN;
  return { width: widthDots, height: y, texts, barcode };
}
